/**
 * Calendar (Календарь) — corporate calendar: holidays, days off, company dates.
 *
 * Tamagui port of the handoff `CalendarScreen` (screen-flow.jsx), re-skinned to
 * the AS Life brand (navy today-cell + bronze month accent instead of the
 * handoff purple):
 *  • month grid — Monday-first, localized weekday header, event-tone day cells,
 *    today highlight, muted weekend fill, event dots + legend
 *  • upcoming panel — next 7 days only (day/month chip, title, kind, KB link)
 *  • today panel — activities on the current day only (общий календарь, not a
 *    planner); flex-fills on desktop so the sidebar matches the grid height
 *
 * Prefers live data: the grid fetches `GET /calendar/events` for the displayed
 * month and both side panels share a date-bounded `GET /calendar/events`
 * (today → today+7) — so "upcoming" is the coming week, not a fixed count.
 * prev/next actually navigate months. When the API is unreachable the grid
 * still renders (weekday shading only, no events/holidays) with an explicit
 * server-unreachable banner (`showServerNotice`) — the old June-2026 demo
 * fallback was removed in 615dae1. Responsive: grid collapses to a single
 * column on phones (sidebar stacks under the month).
 */
import { useEffect, useMemo, useState } from 'react'
import { Platform } from 'react-native'
import { useRouter } from 'expo-router'
import * as WebBrowser from 'expo-web-browser'
import { ScrollView, Stack, Text, XStack, YStack } from 'tamagui'

import { useApp, type Localized, type Lang } from '../../src/i18n'
import { useResponsive } from '../../src/lib/responsive'
import { useToday } from '../../src/lib/useToday'
import { timeAgo } from '../../src/lib/format'
import {
  useCalendarEvents,
  useProductionDays,
  useGraphStatus,
  useGraphConnect,
  useGraphSync,
  useGraphDisconnect,
} from '../../src/features/calendar/useCalendar'
import { isComingSoon } from '../../src/app/nav'
import { tone, type Tone } from '../../src/theme/tones'
import { palette } from '../../src/theme/palette'
import type { ProductionDay } from '../../src/lib/types'
import { Button, Card, Dot, Enter, FadeIn, Icon, IconButton } from '../../src/ui'
import { Page } from '../../src/ui/Screen'

const TERTIARY = '$tertiary'
// bgLayout === background (#FFFFFF) in light mode, so no-event day cells need
// their own tint + hairline border to read as cells (else they render as bare
// numbers on white — the "31 doesn't exist" look). Weekend sits a touch darker.
const CELL_BG = 'rgba(100,116,139,0.045)'
const WEEKEND_BG = 'rgba(100,116,139,0.11)'
// Desktop sidebar: "Сегодня" keeps a floor (header + 1–2 rows always readable) and
// a ceiling so a busy day can't starve "Upcoming"; between them it scrolls inside.
const TODAY_MIN = 210
const TODAY_MAX = 340

// Названия месяцев/дней недели берём у Intl (как fmtDate в src/lib/format), а не из
// рукописных таблиц на четыре языка.
const LOCALE: Record<Lang, string> = { ru: 'ru-RU', en: 'en-US', az: 'az-AZ', ka: 'ka-GE' }
/** ru/az Intl отдаёт «январь»/«пн» строчными — таблицы были с заглавной, сохраняем. */
const up1 = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
const part = (lang: Lang, d: Date, opt: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(LOCALE[lang], opt).format(d)

/** Короткий день недели («Пн»). */
const weekDay = (lang: Lang, d: Date) => up1(part(lang, d, { weekday: 'short' }))
/** Пн-первый заголовок недели (1–7 июня 2026 = Пн…Вс). */
const weekDays = (lang: Lang) => Array.from({ length: 7 }, (_, i) => weekDay(lang, new Date(2026, 5, 1 + i)))
/** Месяц полностью («Январь»). */
const monthLong = (lang: Lang, m: number) => up1(part(lang, new Date(2026, m, 1), { month: 'long' }))
/** Месяц сокращённо, капсом («ЯНВ») — ru отдаёт «янв.», точку убираем. */
const monShortOf = (lang: Lang, m: number) =>
  part(lang, new Date(2026, m, 1), { month: 'short' }).replace(/\.$/, '').toUpperCase()

/** Localized short-month object (for the upcoming day/month chip). */
const monShort = (monthIdx: number): Localized => ({
  ru: monShortOf('ru', monthIdx),
  en: monShortOf('en', monthIdx),
  az: monShortOf('az', monthIdx),
  ka: monShortOf('ka', monthIdx),
})

/** `YYYY-MM-DD` for an event-range query bound. */
const ymd = (y: number, m: number, d: number) =>
  `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`

interface DayCell {
  key: string
  day?: number
  bg: string
  col: string
  dot?: string
  /** Number of events on the day — drives the desktop counter badge. */
  count?: number
  today?: boolean
  /** Corner glyph: '½' сокращённый (предпраздничный) день, '⇄' перенос. */
  mark?: string
}

/** Событие в списке: панель «Сегодня» и слайд-овер дня (без даты — она в шапке). */
interface EvVM {
  key: string
  tone: Tone
  title: Localized
  kind: Localized
  link?: Localized | null
}

/** «Ближайшие» — то же плюс чип с датой. */
interface UpVM extends EvVM {
  day: number
  mon: Localized
}

/** Build the Monday-first month matrix as rows of 7 cells (blanks pad the edges). */
function buildWeeks(
  year: number,
  month: number,
  todayDay: number | null,
  tones: Record<number, Tone>,
  counts: Record<number, number>,
  days: Record<number, ProductionDay>,
): DayCell[][] {
  const startOffset = (new Date(year, month, 1).getDay() + 6) % 7
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  const cells: DayCell[] = []
  for (let i = 0; i < startOffset; i++) cells.push({ key: `b${i}`, bg: 'transparent', col: 'transparent' })

  for (let d = 1; d <= daysInMonth; d++) {
    const dow = (startOffset + d - 1) % 7
    const pd = days[d]
    const evTone = tones[d]
    const cell: DayCell = { key: `d${d}`, day: d, bg: CELL_BG, col: '$color' }

    // ① базовый слой — тип дня из производственного календаря. Фолбэк на день недели,
    //    если данных нет (бэкенд не отдал день) — прежнее поведение по Сб/Вс.
    if (pd) {
      if (pd.dayType === 'saturday' || pd.dayType === 'sunday') {
        cell.bg = WEEKEND_BG
        cell.col = TERTIARY
      } else if (pd.dayType === 'holiday') {
        const tn = tone('red') // праздник также приходит событием (red); подстраховка на пустой слой событий
        cell.bg = tn.soft
        cell.col = tn.text
      } else if (pd.dayType === 'pre_holiday') {
        cell.mark = '½' // сокращённый — остаётся рабочим днём, помечаем бейджем
      }
      if (pd.swapDate) cell.mark = '⇄' // перенос важнее пометки сокращения
    } else if (dow >= 5) {
      cell.bg = WEEKEND_BG
      cell.col = TERTIARY
    }

    // ② слой событий — тон события главнее (праздники/корп-даты), как и раньше
    if (evTone) {
      const tn = tone(evTone)
      cell.bg = tn.soft
      cell.col = tn.text
      cell.dot = tn.solid
      cell.count = counts[d]
    }
    // ③ сегодня — навигационный акцент поверх всего
    if (d === todayDay) {
      cell.bg = palette.navy
      cell.col = '#fff'
      cell.dot = undefined // today drops the narrow dot; desktop keeps the (inverted) counter
      cell.today = true
    }
    cells.push(cell)
  }

  // Pad the final row to a full week of 7.
  while (cells.length % 7 !== 0) cells.push({ key: `t${cells.length}`, bg: 'transparent', col: 'transparent' })

  const weeks: DayCell[][] = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
  return weeks
}

// ── Day count badge (desktop) — mini pill at the cell foot ───────────────────
/** Replaces the dot on wide layouts with a count. Inverted on the navy today cell. */
function DayBadge({ n, bg, color }: { n: number; bg: string; color: string }) {
  return (
    <Stack
      position="absolute"
      bottom={4}
      minWidth={15}
      height={13}
      paddingHorizontal={3}
      borderRadius={999}
      backgroundColor={bg}
      alignItems="center"
      justifyContent="center"
      animation="bouncy"
      enterStyle={{ scale: 0.5, opacity: 0 }}
      scale={1}
      opacity={1}
    >
      <Text fontSize={9} fontWeight="800" color={color} lineHeight={13}>
        {n}
      </Text>
    </Stack>
  )
}

// ── Month grid ───────────────────────────────────────────────────────────────
function MonthGrid({
  year,
  month,
  dir,
  todayDay,
  tones,
  counts,
  days,
  wide,
  onPrev,
  onNext,
  onSelectDay,
}: {
  year: number
  month: number
  /** Last navigation direction: -1 prev, 1 next, 0 initial — drives the grid slide-in. */
  dir: -1 | 0 | 1
  todayDay: number | null
  tones: Record<number, Tone>
  counts: Record<number, number>
  days: Record<number, ProductionDay>
  /** Two-column desktop layout → cells show a count badge; narrow keeps dots. */
  wide: boolean
  onPrev: () => void
  onNext: () => void
  onSelectDay: (day: number) => void
}) {
  const { l, lang } = useApp()
  const weeks = useMemo(
    () => buildWeeks(year, month, todayDay, tones, counts, days),
    [year, month, todayDay, tones, counts, days],
  )
  const wDays = useMemo(() => weekDays(lang), [lang])

  const legend: { color?: string; glyph?: string; label: string }[] = [
    { color: tone('red').solid, label: l('cal.holiday') },
    { color: tone('gold').solid, label: l('cal.corp') },
    { color: WEEKEND_BG, label: l('cal.weekend') },
    { glyph: '½', label: l('cal.shortened') },
    { glyph: '⇄', label: l('cal.swap') },
    { color: palette.navy, label: l('cal.today') },
  ]

  return (
    <Card flex={1} padding={22}>
      {/* month header + prev/next */}
      <XStack alignItems="center" justifyContent="space-between" marginBottom={18}>
        <Text fontSize={16} fontWeight="700" color="$color">
          {monthLong(lang, month)} {year}
        </Text>
        <XStack gap={6}>
          <IconButton borderWidth={1} borderColor="$borderColor" onPress={onPrev}>
            <Icon name="chevron-left" size={16} color={TERTIARY} />
          </IconButton>
          <IconButton borderWidth={1} borderColor="$borderColor" onPress={onNext}>
            <Icon name="chevron-right" size={16} color={TERTIARY} />
          </IconButton>
        </XStack>
      </XStack>

      {/* weekday header */}
      <XStack gap={7} marginBottom={6}>
        {wDays.map((w) => (
          <Text key={w} flexGrow={1} flexBasis={0} minWidth={0} textAlign="center" fontSize={11} fontWeight="600" color={TERTIARY} paddingBottom={4}>
            {w}
          </Text>
        ))}
      </XStack>

      {/* day cells — keyed by month so prev/next re-mounts the grid with a
          fade + slide from the travel direction (fade+rise on first mount) */}
      <FadeIn
        key={`${year}-${month}`}
        gap={7}
        x={0}
        enterStyle={{ opacity: 0, x: dir * 16, y: dir ? 0 : 10 }}
      >
        {weeks.map((week, wi) => (
          <XStack key={wi} gap={7}>
            {week.map((c) =>
              c.day == null ? (
                <Stack key={c.key} flexGrow={1} flexBasis={0} minWidth={0} aspectRatio={1} />
              ) : (
                <Stack
                  key={c.key}
                  flexGrow={1}
                  flexBasis={0}
                  minWidth={0}
                  aspectRatio={1}
                  borderRadius={5}
                  borderWidth={1}
                  borderColor={c.today ? palette.navy : '$borderSecondary'}
                  alignItems="center"
                  justifyContent="center"
                  backgroundColor={c.bg}
                  cursor="pointer"
                  animation="quicker"
                  hoverStyle={{ opacity: 0.82 }}
                  pressStyle={{ opacity: 0.65, scale: 0.97 }}
                  onPress={() => onSelectDay(c.day!)}
                  {...(c.today
                    ? {
                        shadowColor: palette.navy,
                        shadowOpacity: 0.35,
                        shadowRadius: 8,
                        shadowOffset: { width: 0, height: 2 },
                      }
                    : null)}
                >
                  <Text fontSize={13} fontWeight="700" color={c.col}>
                    {c.day}
                  </Text>
                  {c.mark && (
                    <Text
                      position="absolute"
                      top={3}
                      right={4}
                      fontSize={9}
                      fontWeight="800"
                      color={c.today ? '#fff' : TERTIARY}
                    >
                      {c.mark}
                    </Text>
                  )}
                  {wide
                    ? // desktop: count badge (today inverts to read on navy)
                      c.count != null && (
                        <DayBadge
                          n={c.count}
                          bg={c.today ? '#fff' : (c.dot as string)}
                          color={c.today ? palette.navy : '#fff'}
                        />
                      )
                    : // narrow: original event dot (today has none)
                      c.dot && (
                        <Stack
                          position="absolute"
                          bottom={6}
                          width={5}
                          height={5}
                          borderRadius={999}
                          backgroundColor={c.dot}
                          animation="bouncy"
                          enterStyle={{ scale: 0.5, opacity: 0 }}
                          scale={1}
                          opacity={1}
                        />
                      )}
                </Stack>
              ),
            )}
          </XStack>
        ))}
      </FadeIn>

      {/* legend */}
      <XStack gap={18} flexWrap="wrap" marginTop={18} paddingTop={16} borderTopWidth={1} borderColor="$borderSecondary">
        {legend.map((lg) => (
          <XStack key={lg.label} alignItems="center" gap={7}>
            {lg.glyph ? (
              <Stack
                width={13}
                height={13}
                borderRadius={4}
                borderWidth={1}
                borderColor="$borderColor"
                alignItems="center"
                justifyContent="center"
              >
                <Text fontSize={9} fontWeight="800" color="$muted" lineHeight={11}>
                  {lg.glyph}
                </Text>
              </Stack>
            ) : (
              <Stack width={11} height={11} borderRadius={4} backgroundColor={lg.color} />
            )}
            <Text fontSize={11} color="$muted">
              {lg.label}
            </Text>
          </XStack>
        ))}
      </XStack>
    </Card>
  )
}

// ── Empty state (shared by the week / today panels) ──────────────────────────
function CalEmpty({ icon, text, fill }: { icon: string; text: string; fill?: boolean }) {
  return (
    <YStack
      {...(fill ? { flex: 1 } : null)}
      alignItems="center"
      justifyContent="center"
      gap={9}
      paddingVertical={fill ? 24 : 16}
    >
      <Stack
        width={38}
        height={38}
        borderRadius={999}
        backgroundColor={tone('slate').soft}
        alignItems="center"
        justifyContent="center"
      >
        <Icon name={icon} size={18} color={TERTIARY} />
      </Stack>
      <Text fontSize={12} color="$muted" textAlign="center" maxWidth={220}>
        {text}
      </Text>
    </YStack>
  )
}

// ── Event link → action button (hides raw URLs) ──────────────────────────────
/** Open an http(s) link in the system browser (new tab on web, in-app on native). */
function openEventUrl(href: string) {
  if (Platform.OS === 'web') globalThis.open?.(href, '_blank', 'noopener,noreferrer')
  else WebBrowser.openBrowserAsync(href).catch(() => {})
}

const isHttpUrl = (s: string) => /^https?:\/\//i.test(s)

/**
 * Renders an event's `link` as a compact action button instead of raw link text.
 * Outlook/Graph events carry a kilometre-long `webLink` URL — we hide it behind an
 * «Открыть» button that opens the browser. Editorial events carry a short localized
 * label that routes to the KB (kept behind the existing coming-soon gate). Returns
 * null when there's nothing to link.
 */
function EventLinkButton({ link, onNavigate }: { link?: Localized | null; onNavigate?: () => void }) {
  const { t, l } = useApp()
  const router = useRouter()
  if (!link) return null
  const raw = (t(link) ?? '').trim()
  if (!raw) return null

  if (isHttpUrl(raw)) {
    return (
      <Button
        size="sm"
        minHeight={36}
        alignSelf="flex-start"
        icon={<Icon name="external-link" size={14} color="$primary" />}
        onPress={() => openEventUrl(raw)}
      >
        {l('cal.openLink')}
      </Button>
    )
  }

  if (isComingSoon('/kb')) return null
  return (
    <Button
      size="sm"
      minHeight={36}
      alignSelf="flex-start"
      icon={<Icon name="book-open" size={14} color="$primary" />}
      onPress={() => {
        onNavigate?.()
        router.push('/kb')
      }}
    >
      {l('cal.details')}
    </Button>
  )
}

/** Small count pill shown in the upcoming / today card headers. */
function CountChip({ n }: { n: number }) {
  const sl = tone('slate')
  return (
    <Stack
      backgroundColor={sl.soft}
      borderRadius={999}
      paddingHorizontal={9}
      paddingVertical={3}
      minWidth={26}
      alignItems="center"
    >
      <Text fontSize={11} fontWeight="700" color={sl.text}>
        {n}
      </Text>
    </Stack>
  )
}

// ── Upcoming events (next 7 days only) ───────────────────────────────────────
// Header stays fixed; the list scrolls inside a capped-height area so a busy week
// never stretches the card (avoids the "kilometre-tall card" the API data caused).
function Upcoming({ items, fill }: { items: UpVM[]; fill?: boolean }) {
  const { t, l } = useApp()
  const { isPhone } = useResponsive()

  return (
    <Card padding={20} {...(fill ? { flex: 1, minHeight: 0 } : null)}>
      <XStack alignItems="flex-start" justifyContent="space-between" gap={8}>
        <YStack flex={1} minWidth={0}>
          <Text fontSize={14} fontWeight="700" color="$color">
            {l('cal.upcoming')}
          </Text>
          <Text fontSize={11} color="$muted" marginTop={3}>
            {l('cal.upcomingWeek')}
          </Text>
        </YStack>
        {items.length > 0 && <CountChip n={items.length} />}
      </XStack>

      {items.length === 0 ? (
        <CalEmpty icon="calendar" text={l('cal.weekEmpty')} fill={fill} />
      ) : (
        <ScrollView
          {...(fill ? { flex: 1, minHeight: 0 } : { maxHeight: isPhone ? 340 : 300 })}
          marginTop={6}
          nestedScrollEnabled
        >
          {items.map((e, i) => {
            const tn = tone(e.tone)
            return (
              <Enter key={e.key} index={Math.min(i, 11)}>
              <XStack
                gap={13}
                paddingVertical={11}
                {...(i ? { borderTopWidth: 1, borderColor: '$borderSecondary' } : null)}
              >
                {/* day / month chip */}
                <YStack width={44} flexShrink={0} alignItems="center">
                  <Text fontSize={18} fontWeight="800" color={tn.text} lineHeight={20}>
                    {e.day}
                  </Text>
                  <Text fontSize={10} color={TERTIARY} marginTop={2}>
                    {t(e.mon)}
                  </Text>
                </YStack>
                {/* title + kind + optional link */}
                <YStack flex={1} minWidth={0} gap={6}>
                  <Text fontSize={13} fontWeight="600" color="$color" lineHeight={18}>
                    {t(e.title)}
                  </Text>
                  <XStack
                    alignSelf="flex-start"
                    backgroundColor={tn.soft}
                    borderRadius={999}
                    paddingHorizontal={9}
                    paddingVertical={3}
                  >
                    <Text fontSize={11} fontWeight="700" color={tn.text}>
                      {t(e.kind)}
                    </Text>
                  </XStack>
                  <EventLinkButton link={e.link} />
                </YStack>
              </XStack>
              </Enter>
            )
          })}
        </ScrollView>
      )}
    </Card>
  )
}

// ── Today's activities (current day only — общий календарь, не планировщик) ───
function TodayCard({ items, fill }: { items: EvVM[]; fill?: boolean }) {
  const { t, l, lang } = useApp()
  const { isPhone } = useResponsive()

  const now = new Date()
  const dateLabel = `${weekDay(lang, now)}, ${now.getDate()} ${monShortOf(lang, now.getMonth())}`

  return (
    <Card padding={20} {...(fill ? { flex: 1, minHeight: 0 } : null)}>
      {/* header — navy chip echoes the "today" cell in the grid */}
      <XStack alignItems="center" gap={11} marginBottom={items.length ? 14 : 4}>
        <Stack
          width={36}
          height={36}
          borderRadius={9}
          backgroundColor={palette.navy}
          alignItems="center"
          justifyContent="center"
        >
          <Icon name="calendar" size={17} color="#fff" />
        </Stack>
        <YStack flex={1} minWidth={0}>
          <Text fontSize={14} fontWeight="700" color="$color">
            {l('cal.today')}
          </Text>
          <Text fontSize={11} color={TERTIARY} marginTop={1}>
            {dateLabel}
          </Text>
        </YStack>
        {items.length > 0 && <CountChip n={items.length} />}
      </XStack>

      {items.length === 0 ? (
        <CalEmpty icon="check-circle" text={l('cal.todayEmpty')} fill={fill} />
      ) : (
        <ScrollView
          {...(fill ? { flex: 1, minHeight: 0 } : { maxHeight: isPhone ? 320 : 300 })}
          nestedScrollEnabled
        >
          {items.map((e, i) => {
            const tn = tone(e.tone)
            return (
              <XStack
                key={e.key}
                gap={11}
                paddingVertical={10}
                alignItems="flex-start"
                {...(i ? { borderTopWidth: 1, borderColor: '$borderSecondary' } : null)}
              >
                <Stack width={9} height={9} borderRadius={999} backgroundColor={tn.solid} marginTop={5} />
                <YStack flex={1} minWidth={0} gap={6}>
                  <Text fontSize={13} fontWeight="600" color="$color" lineHeight={18}>
                    {t(e.title)}
                  </Text>
                  <XStack
                    alignSelf="flex-start"
                    backgroundColor={tn.soft}
                    borderRadius={999}
                    paddingHorizontal={9}
                    paddingVertical={3}
                  >
                    <Text fontSize={11} fontWeight="700" color={tn.text}>
                      {t(e.kind)}
                    </Text>
                  </XStack>
                  <EventLinkButton link={e.link} />
                </YStack>
              </XStack>
            )
          })}
        </ScrollView>
      )}
    </Card>
  )
}

// ── Day events — AS-Ассистент-style slide-over for a tapped date ─────────────
function DayEventsPanel({
  open,
  year,
  month,
  day,
  events,
  onClose,
}: {
  open: boolean
  year: number
  month: number
  day: number | null
  events: EvVM[]
  onClose: () => void
}) {
  const { t, l, lang } = useApp()
  const { isPhone } = useResponsive()

  // Esc closes the panel (web) — same affordance as the assistant.
  useEffect(() => {
    if (!open || Platform.OS !== 'web') return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open || day == null) return null

  const isWeb = Platform.OS === 'web'
  const weekday = weekDay(lang, new Date(year, month, day))

  return (
    <YStack
      position={isWeb ? ('fixed' as 'absolute') : 'absolute'}
      top={0}
      left={0}
      right={0}
      bottom={0}
      zIndex={200}
    >
      {/* backdrop */}
      <Stack
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        backgroundColor={palette.scrim}
        onPress={onClose}
      />

      {/* panel — pinned right (full-screen on phone), same chrome as AssistantPanel */}
      <YStack
        position="absolute"
        top={0}
        right={0}
        bottom={0}
        width={isPhone ? '100%' : 420}
        maxWidth="100%"
        backgroundColor="$background"
        borderLeftWidth={1}
        borderColor="$borderSecondary"
        shadowColor={palette.shadow}
        shadowOpacity={0.22}
        shadowRadius={28}
        shadowOffset={{ width: -8, height: 0 }}
      >
        {/* header — calendar chip + date + close */}
        <XStack
          alignItems="center"
          gap={11}
          paddingHorizontal={20}
          height={64}
          borderBottomWidth={1}
          borderColor="$borderSecondary"
        >
          <Stack width={34} height={34} borderRadius={9} backgroundColor={palette.navy} alignItems="center" justifyContent="center">
            <Icon name="calendar" size={17} color="#fff" />
          </Stack>
          <YStack flex={1} minWidth={0}>
            <Text fontSize={15} fontWeight="700" color="$color">
              {day} {monShortOf(lang, month)} {year}
            </Text>
            <Text fontSize={11} color={TERTIARY}>
              {weekday} · {l('cal.dayEvents')}
            </Text>
          </YStack>
          <IconButton onPress={onClose}>
            <Icon name="x" size={18} color="$muted" />
          </IconButton>
        </XStack>

        {/* body — event cards (AS-Ассистент result-card style) or empty state */}
        <ScrollView flex={1} contentContainerStyle={{ padding: 20, gap: 10 }}>
          {events.length === 0 ? (
            <CalEmpty icon="calendar" text={l('cal.dayEmpty')} />
          ) : (
            events.map((e) => {
              const tn = tone(e.tone)
              return (
                <YStack key={e.key} gap={e.link ? 10 : 0} padding={13} borderWidth={1} borderColor="$borderSecondary" borderRadius={9}>
                  <XStack gap={11} alignItems="center">
                    <Stack width={36} height={36} borderRadius={8} backgroundColor={tn.soft} alignItems="center" justifyContent="center">
                      <Icon name="calendar" size={16} color={tn.text} />
                    </Stack>
                    <YStack flex={1} minWidth={0} gap={6}>
                      <Text fontSize={14} fontWeight="600" color="$color" lineHeight={19}>
                        {t(e.title)}
                      </Text>
                      <XStack alignSelf="flex-start" backgroundColor={tn.soft} borderRadius={999} paddingHorizontal={9} paddingVertical={3}>
                        <Text fontSize={11} fontWeight="700" color={tn.text}>
                          {t(e.kind)}
                        </Text>
                      </XStack>
                    </YStack>
                  </XStack>
                  <EventLinkButton link={e.link} onNavigate={onClose} />
                </YStack>
              )
            })
          )}
        </ScrollView>
      </YStack>
    </YStack>
  )
}

// ── Mail-import toolbar (Microsoft 365 / Graph, 🔶) ───────────────────────────
// One compact row above/below the grid (was a sidebar card). Same hooks + Entra
// consent-return handling; wraps (flexWrap) instead of collapsing on narrow.
function MailToolbar() {
  const { t } = useApp()
  const { data: status, refetch } = useGraphStatus()
  const connect = useGraphConnect()
  const sync = useGraphSync()
  const disconnect = useGraphDisconnect()

  // Return from Entra consent on web (?calendar=connected|error) → refetch + clean URL.
  useEffect(() => {
    if (Platform.OS !== 'web') return
    const loc = globalThis.location
    if (!loc) return
    const sp = new URLSearchParams(loc.search)
    if (!sp.has('calendar')) return
    refetch()
    sp.delete('calendar')
    globalThis.history?.replaceState?.({}, '', loc.pathname + (sp.toString() ? `?${sp.toString()}` : ''))
  }, [refetch])

  // Integration not configured on the backend (or endpoint absent) → hide entirely.
  if (!status?.configured) return null

  const revoked = status.status === 'revoked'
  const connected = status.connected && !revoked

  const onConnect = async () => {
    const res = await connect.mutateAsync().catch(() => null)
    if (!res?.url) return
    if (Platform.OS === 'web') globalThis.location?.assign(res.url)
    else {
      await WebBrowser.openAuthSessionAsync(res.url).catch(() => {})
      refetch()
    }
  }

  return (
    <Card paddingVertical={10} paddingHorizontal={14} gap={connected || status.lastError ? 8 : 0}>
      <XStack alignItems="center" gap={10} flexWrap="wrap">
        {/* mail icon + label */}
        <Stack width={30} height={30} borderRadius={7} backgroundColor="$primaryBg" alignItems="center" justifyContent="center">
          <Icon name="mail" size={16} color="$primary" />
        </Stack>
        <Text fontSize={13} fontWeight="700" color="$color">
          {t({ ru: 'Почтовый календарь', en: 'Mail calendar' })}
        </Text>

        {/* status + dot */}
        <XStack alignItems="center" gap={6}>
          <Dot tone={connected ? 'green' : revoked ? 'red' : 'default'} />
          <Text fontSize={12} color="$muted" numberOfLines={1}>
            {connected
              ? t({ ru: 'Подключён', en: 'Connected' })
              : revoked
                ? t({ ru: 'Доступ истёк', en: 'Access expired' })
                : t({ ru: 'Не подключён', en: 'Not connected' })}
          </Text>
        </XStack>

        {/* last-synced note */}
        {connected && !!status.lastSyncedAt && (
          <Text fontSize={12} color="$muted" numberOfLines={1}>
            · {t({ ru: 'Обновлено', en: 'Synced' })} {timeAgo(status.lastSyncedAt)}
          </Text>
        )}

        {/* actions — right-aligned; wrap to a new line on narrow rows */}
        <XStack marginLeft="auto" gap={8} flexShrink={0}>
          {connected ? (
            <>
              <Button
                size="sm"
                icon={<Icon name="refresh-cw" size={14} color="$muted" />}
                onPress={() => sync.mutate()}
                opacity={sync.isPending ? 0.5 : 1}
              >
                {sync.isPending ? t({ ru: 'Обновляю…', en: 'Syncing…' }) : t({ ru: 'Обновить', en: 'Sync' })}
              </Button>
              <Button
                size="sm"
                icon={<Icon name="x" size={14} color="$muted" />}
                onPress={() => disconnect.mutate()}
                opacity={disconnect.isPending ? 0.5 : 1}
              >
                {t({ ru: 'Отключить', en: 'Disconnect' })}
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              size="sm"
              icon={<Icon name="calendar" size={14} color="#fff" />}
              onPress={onConnect}
              opacity={connect.isPending ? 0.6 : 1}
            >
              {revoked ? t({ ru: 'Переподключить', en: 'Reconnect' }) : t({ ru: 'Подключить календарь', en: 'Connect calendar' })}
            </Button>
          )}
        </XStack>
      </XStack>

      {!!status.lastError && !connected && (
        <Text fontSize={11} color={tone('red').text} numberOfLines={2}>
          {status.lastError}
        </Text>
      )}
    </Card>
  )
}

export default function CalendarScreen() {
  const { l, t } = useApp()
  const { isWide } = useResponsive()

  // «Сегодня» — стабильная Date в пределах дня (иначе new Date() пересоздавался на каждый
  // рендер, и всё производное пересчитывалось при любом чужом ре-рендере), в полночь
  // обновляется сама — экран может висеть открытым сутками.
  const now = useToday()
  const [cursor, setCursor] = useState({ year: now.getFullYear(), month: now.getMonth() })
  // Measured natural height of the month-grid column — the sidebar is pinned to
  // this on desktop so its cards can never overshoot the calendar (they flex to
  // share the height and scroll internally instead).
  const [gridH, setGridH] = useState<number | null>(null)
  // Two-column (grid + sidebar) only when the real row is wide enough; below that
  // we stack. Window-based breakpoints don't see the nav rail (or devtools), so
  // measure the actual container width and switch on it instead of `isPhone`.
  const [rowW, setRowW] = useState<number | null>(null)
  const twoCol = rowW == null ? isWide : rowW >= 820
  const [dir, setDir] = useState<-1 | 0 | 1>(0) // last prev/next direction → grid slide-in
  const [selectedDay, setSelectedDay] = useState<number | null>(null)

  const from = ymd(cursor.year, cursor.month, 1)
  const to = ymd(cursor.year, cursor.month, new Date(cursor.year, cursor.month + 1, 0).getDate())

  // Today + the next 7 days drive both side panels — one date-bounded query
  // (not a fixed count), so "upcoming" means strictly the coming week.
  const todayYmd = ymd(now.getFullYear(), now.getMonth(), now.getDate())
  const weekEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7)
  const weekEndYmd = ymd(weekEnd.getFullYear(), weekEnd.getMonth(), weekEnd.getDate())

  const { data: liveEvents, isError: eventsError } = useCalendarEvents(from, to)
  const { data: prodDays, isError: prodDaysError } = useProductionDays(from, to)
  const weekQuery = useCalendarEvents(todayYmd, weekEndYmd)
  const liveWeek = weekQuery.data

  // Плашка «нет связи с сервером» — только когда месячные запросы (сетка) реально
  // упали в ошибку. isLoading сюда не попадает: react-query не бывает одновременно
  // в isError и в состоянии загрузки (статусы взаимоисключающие), а пустой успешный
  // ответ (data = []) — валидное «в этом месяце событий нет», не повод для плашки.
  const showServerNotice = eventsError || prodDaysError
  const noticeTone = tone('gold')
  const noticeText = t({
    ru: 'Нет связи с сервером — события и праздники не показаны',
    en: 'Server unreachable — events and holidays are not shown',
  })

  // День месяца → тип дня производственного календаря + события дня + тон/счётчик
  // клетки. Пересчитываем только когда реально меняются данные месяца — раньше эти
  // циклы (с new Date() на каждый элемент) гонялись на КАЖДЫЙ рендер экрана, включая
  // ресайз (gridH/rowW из onLayout) и открытие/закрытие слайдовера дня. cursor
  // отдельной зависимостью не нужен: он входит в queryKey (from/to), поэтому при его
  // смене prodDays/liveEvents меняют ссылку (на undefined на время загрузки либо на
  // новые данные) уже в этом же рендере — react-query не хранит keepPreviousData тут.
  const { days, dayEvents, tones, counts } = useMemo(() => {
    const days: Record<number, ProductionDay> = {}
    for (const pd of prodDays ?? []) {
      const [y, m, dd] = pd.date.split('-').map(Number)
      if (y === cursor.year && m - 1 === cursor.month) days[dd] = pd
    }

    // Day → events for the displayed month. Tapping a cell opens these.
    const dayEvents: Record<number, EvVM[]> = {}
    for (const e of liveEvents ?? []) {
      const d = new Date(e.date)
      if (d.getFullYear() === cursor.year && d.getMonth() === cursor.month) {
        const day = d.getDate()
        ;(dayEvents[day] ??= []).push({ key: e.id, tone: e.tone, title: e.title, kind: e.kind, link: e.link })
      }
    }

    // Grid cell tone — first event of the day wins; count drives the desktop badge.
    const tones: Record<number, Tone> = {}
    const counts: Record<number, number> = {}
    for (const [day, evs] of Object.entries(dayEvents)) {
      tones[Number(day)] = evs[0].tone
      counts[Number(day)] = evs.length
    }

    return { days, dayEvents, tones, counts }
  }, [prodDays, liveEvents])

  // Highlight today only when the displayed month is the actual current month.
  const todayDay =
    cursor.year === now.getFullYear() && cursor.month === now.getMonth() ? now.getDate() : null

  // ── Side panels: "today" (current day) + "this week" (next 7 days) ──────────
  const isToday = (iso: string) => {
    const d = new Date(iso)
    return (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    )
  }

  // Upcoming — next 7 days, excluding today (today has its own card below). liveWeek
  // is the only real dependency: monShort() below returns all 4 languages eagerly
  // (Upcoming/TodayCard pick one via t() at render time), so lang doesn't belong here.
  const upcoming: UpVM[] = useMemo(
    () =>
      (liveWeek ?? [])
        .filter((e) => !isToday(e.date))
        .slice()
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((e) => {
          const d = new Date(e.date)
          return {
            key: e.id,
            day: d.getDate(),
            mon: monShort(d.getMonth()),
            tone: e.tone,
            title: e.title,
            kind: e.kind,
            link: e.link,
          }
        }),
    [liveWeek],
  )

  // Today — current day only.
  const todayItems: EvVM[] = useMemo(
    () =>
      (liveWeek ?? [])
        .filter((e) => isToday(e.date))
        .map((e) => ({ key: e.id, tone: e.tone, title: e.title, kind: e.kind, link: e.link })),
    [liveWeek],
  )

  const step = (delta: number) => {
    setSelectedDay(null) // close the day panel when the month changes
    setDir(delta < 0 ? -1 : 1)
    setCursor((c) => {
      const m = c.month + delta
      return { year: c.year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 }
    })
  }

  return (
    <>
      <Page title={l('cal.title')} subtitle={l('cal.subtitle')} maxWidth={1100}>
        {/* Desktop: the grid takes its natural height (alignItems="flex-start"
            keeps the row from stretching it to the sidebar's content) and we
            measure it via onLayout; the sidebar is then pinned to exactly that
            height, so its two list cards flex to share it and scroll internally
            — the right column can never overshoot the calendar. Phone: stacks. */}
        <XStack
          gap={16}
          flexDirection={twoCol ? 'row' : 'column'}
          alignItems={twoCol ? 'flex-start' : 'stretch'}
          onLayout={(e) => {
            const w = Math.round(e.nativeEvent.layout.width)
            setRowW((prev) => (prev === w ? prev : w))
          }}
        >
          {/* cascade: grid (+ mail toolbar) enters as one block, sidebar cards follow.
              The toolbar sits under the grid so it never competes with the month
              header, and is inside the measured column so the sidebar height-matches
              the whole left column. It renders null when the integration is
              unconfigured (single grid child → gap adds no phantom space). */}
          <Enter
            index={0}
            flex={1}
            minWidth={0}
            onLayout={(e) => {
              const h = Math.round(e.nativeEvent.layout.height)
              setGridH((prev) => (prev === h ? prev : h))
            }}
          >
            <YStack gap={16}>
              {/* Сервер недоступен — грид без событий/праздников; предупреждаем явно,
                  чтобы «чистый» месяц не приняли за факт. */}
              {showServerNotice && (
                <XStack
                  alignSelf="stretch"
                  role="alert"
                  accessibilityLabel={noticeText}
                  backgroundColor={noticeTone.soft}
                  borderWidth={1}
                  borderColor={noticeTone.soft}
                  borderLeftWidth={3}
                  borderLeftColor={noticeTone.solid}
                  borderRadius={7}
                  gap={10}
                  paddingVertical={11}
                  paddingHorizontal={12}
                  alignItems="center"
                >
                  <Icon name="triangle-alert" size={16} color={noticeTone.text} />
                  <Text fontSize={12} fontWeight="700" color={noticeTone.text} flex={1}>
                    {noticeText}
                  </Text>
                </XStack>
              )}
              <MonthGrid
                year={cursor.year}
                month={cursor.month}
                dir={dir}
                todayDay={todayDay}
                tones={tones}
                counts={counts}
                days={days}
                wide={twoCol}
                onPrev={() => step(-1)}
                onNext={() => step(1)}
                onSelectDay={setSelectedDay}
              />
              <MailToolbar />
            </YStack>
          </Enter>
          <YStack
            width={twoCol ? 380 : '100%'}
            flexShrink={0}
            gap={16}
            // Pin the sidebar to the measured grid height (two-column only). Until
            // the first onLayout lands it renders unconstrained for one frame.
            {...(twoCol && gridH ? { height: gridH, overflow: 'hidden' } : null)}
          >
            {/* Upcoming takes the remainder (grow + basis:0) and scrolls inside.
                Pinned flex only applies once gridH is measured; pre-measure it keeps
                the old content-sized flex so the sidebar doesn't flash collapsed. */}
            <Enter
              index={1}
              {...(twoCol
                ? gridH
                  ? { flexGrow: 1, flexShrink: 1, flexBasis: 0, minHeight: 0 }
                  : { flex: 1, minHeight: 0 }
                : null)}
            >
              <Upcoming items={upcoming} fill={twoCol} />
            </Enter>
            {/* Today is content-sized between a floor and a ceiling so it's never
                squished by a long Upcoming (and scrolls inside past the ceiling). */}
            <Enter
              index={2}
              {...(twoCol
                ? gridH
                  ? { flexGrow: 0, flexShrink: 0, flexBasis: 'auto', minHeight: TODAY_MIN, maxHeight: TODAY_MAX }
                  : { minHeight: TODAY_MIN }
                : null)}
            >
              <TodayCard items={todayItems} fill={twoCol} />
            </Enter>
          </YStack>
        </XStack>
        <Stack height={8} />
      </Page>

      <DayEventsPanel
        open={selectedDay != null}
        year={cursor.year}
        month={cursor.month}
        day={selectedDay}
        events={selectedDay != null ? (dayEvents[selectedDay] ?? []) : []}
        onClose={() => setSelectedDay(null)}
      />
    </>
  )
}
